import type { ReactNode } from 'react';
import { ArrowUpRight, LockKeyhole, RefreshCw, ShieldAlert } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import type { CompactFeatureSnapshot, FeatureId } from '@grid2/shared-types';
import { useSession } from '../../auth/SessionContext';

export type FeatureUiState =
  | 'enabled'
  | 'upgrade'
  | 'disabled'
  | 'blocked'
  | 'hidden'
  | 'unavailable';

export interface FeatureUiDecision {
  id: FeatureId;
  state: FeatureUiState;
  enabled: boolean;
  visible: boolean;
  blockedBy: string[];
}

const EMPTY_FEATURES: CompactFeatureSnapshot = {
  catalogVersion: '0.0',
  policyVersion: 0,
  authorityEpoch: 0,
  enabled: [],
  upgrade: [],
  hidden: [],
  blocked: {},
  unavailable: true,
};

export function featureDecision(
  snapshot: CompactFeatureSnapshot | undefined,
  id: FeatureId,
): FeatureUiDecision {
  const features = snapshot ?? EMPTY_FEATURES;
  if (features.unavailable) {
    return { id, state: 'unavailable', enabled: false, visible: true, blockedBy: [] };
  }
  if (features.enabled.includes(id)) {
    return { id, state: 'enabled', enabled: true, visible: true, blockedBy: [] };
  }
  if (features.upgrade.includes(id)) {
    return { id, state: 'upgrade', enabled: false, visible: true, blockedBy: [] };
  }
  if (features.hidden.includes(id)) {
    return { id, state: 'hidden', enabled: false, visible: false, blockedBy: [] };
  }
  const blockedBy = features.blocked[id] ?? [];
  if (blockedBy.length > 0) {
    return { id, state: 'blocked', enabled: false, visible: true, blockedBy };
  }
  return { id, state: 'disabled', enabled: false, visible: true, blockedBy: [] };
}

export function useFeature(id: FeatureId): FeatureUiDecision {
  const { features } = useSession();
  return featureDecision(features, id);
}

export function useEnabledFeatureIds(): ReadonlySet<string> {
  const { features } = useSession();
  return new Set(features.enabled);
}

export function Feature({
  id,
  children,
  fallback = null,
  showWhen = 'enabled',
}: {
  id: FeatureId;
  children: ReactNode;
  fallback?: ReactNode;
  showWhen?: 'enabled' | 'visible';
}) {
  const decision = useFeature(id);
  const show = showWhen === 'enabled' ? decision.enabled : decision.visible;
  return show ? children : fallback;
}

function unavailableCopy(state: FeatureUiState) {
  if (state === 'upgrade') {
    return {
      eyebrow: 'Available on another plan',
      title: 'Upgrade to use this feature',
      description:
        'This capability is not included in your current plan. Organization admins can review available upgrades.',
    };
  }
  if (state === 'blocked') {
    return {
      eyebrow: 'Requirement disabled',
      title: 'This feature needs another feature',
      description:
        'One or more required capabilities are disabled. An organization admin can review the dependency and enable the complete set.',
    };
  }
  if (state === 'unavailable') {
    return {
      eyebrow: 'Availability check delayed',
      title: 'Feature availability could not be verified',
      description:
        'Grid2 could not safely verify this organization’s current feature policy. Refresh before trying again.',
    };
  }
  return {
    eyebrow: 'Disabled for this organization',
    title: 'This feature is not enabled',
    description:
      'An organization admin can enable this capability when it is included in the current plan.',
  };
}

export function FeatureUnavailable({
  decision,
  title,
}: {
  decision: FeatureUiDecision;
  title?: string;
}) {
  const { isAppAdmin, refresh } = useSession();
  const copy = unavailableCopy(decision.state);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-3xl items-center px-4 py-10 sm:px-6">
      <section className="w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_65px_rgba(15,23,42,0.09)]">
        <div className="bg-[radial-gradient(circle_at_top_right,rgba(214,64,65,0.22),transparent_20rem)] bg-slate-950 p-6 text-white sm:p-9">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-red-300">
            {decision.state === 'unavailable'
              ? <ShieldAlert className="h-6 w-6" aria-hidden="true" />
              : <LockKeyhole className="h-6 w-6" aria-hidden="true" />}
          </span>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-red-300">{copy.eyebrow}</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">{title ?? copy.title}</h1>
          <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">{copy.description}</p>
        </div>
        <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:p-6">
          {decision.state === 'unavailable' ? (
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Refresh availability
            </button>
          ) : isAppAdmin ? (
            <Link
              to="/organization-admin?section=features"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              Review organization features
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : (
            <Link
              to="/billing"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              View plan
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
          <Link
            to="/settings"
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back to settings
          </Link>
        </div>
      </section>
    </div>
  );
}

export function FeatureRoute({
  id,
  children,
  title,
}: {
  id: FeatureId;
  children: ReactNode;
  title?: string;
}) {
  const decision = useFeature(id);
  if (decision.state === 'hidden') return <Navigate to="/settings" replace />;
  if (!decision.enabled) return <FeatureUnavailable decision={decision} title={title} />;
  return children;
}
